import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    this.addEventListener('play', event => {
      if (!(event.target instanceof HTMLVideoElement)) return;
      this.querySelectorAll('video').forEach(video => { if (video !== event.target) video.pause(); });
    }, { capture: true, signal });
    this.#videoObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => { if (!entry.isIntersecting) entry.target.pause(); });
    }, { threshold: 0.1 });
    this.querySelectorAll('video').forEach(video => this.#videoObserver.observe(video));
    const target = this.closest('.shopify-section, dialog');

    let checkedOption;
    const rememberOption = event => {
      const input = event.target.closest('variant-picker input[type="radio"], variant-picker label')?.querySelector?.('input[type="radio"]') || event.target.closest('variant-picker input[type="radio"]');
      checkedOption = input?.checked ? input : null;
    };
    target?.addEventListener('pointerdown', rememberOption, { signal });
    target?.addEventListener('keydown', rememberOption, { signal });
    target?.addEventListener('click', event => {
      const input = checkedOption;
      checkedOption = null;
      if (input && (event.target === input || event.target.closest('label')?.contains(input))) {
        input.closest('variant-picker')?.variantChanged({ target: input });
      }
    }, { signal });

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
  #videoObserver;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#videoObserver?.disconnect();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const mediaId = newMediaGallery.dataset.selectedMediaId;
        const currentSlides = [...this.querySelectorAll('slideshow-component[ref="slideshow"] slideshow-slide')];
        const index = currentSlides.findIndex(slide => slide.querySelector(`[data-media-id="${mediaId}"]`));
        this.querySelectorAll('video').forEach(video => video.pause());
        if (index >= 0 && this.slideshow) {
          this.slideshow.select(index, undefined, { animate: false });
          this.dataset.selectedMediaId = mediaId;
        } else {
          this.replaceWith(newMediaGallery);
          requestAnimationFrame(() => requestAnimationFrame(() => {
            const slides = [...newMediaGallery.querySelectorAll('slideshow-component[ref="slideshow"] slideshow-slide')];
            const targetIndex = slides.findIndex(slide => slide.querySelector(`[data-media-id="${mediaId}"]`));
            if (targetIndex >= 0) newMediaGallery.slideshow?.select(targetIndex, undefined, { animate: false });
          }));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
